import React from 'react';
import Icon from '../../../components/AppIcon';

const toSafeInsights = (items, fallbackTitle) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (!item) return null;

      if (typeof item === 'string') {
        return { title: item, description: '', transcriptRef: 0 };
      }

      if (typeof item !== 'object') return null;

      return {
        ...item,
        title: item.title || item.point || fallbackTitle,
        description: item.description || item.reason || item.impact || '',
        suggestion: item.suggestion || item.impact || '',
        transcriptRef: Number(item.transcriptRef) || 0,
      };
    })
    .filter(Boolean);
};

const StrengthsWeaknessesSection = ({ strengths = [], weaknesses = [], onTranscriptRefClick }) => {
  const safeStrengths = toSafeInsights(strengths, 'Strength');
  const safeWeaknesses = toSafeInsights(weaknesses, 'Area to improve');

  const handleRefClick = (refId) => {
    if (onTranscriptRefClick) {
      onTranscriptRefClick(refId);
    } else {
      const el = document.getElementById(`transcript-turn-${refId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-emerald-400', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400', 'ring-offset-2'), 3000);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
      {/* Strengths */}
      <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
            <Icon name="TrendingUp" size={14} className="text-emerald-500" />
          </div>
          <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Strengths</h3>
          <span className="text-[9px] font-bold text-slate-400 ml-auto">{safeStrengths.length} identified</span>
        </div>
        <div className="space-y-3">
          {safeStrengths.map((s, idx) => (
            <div key={idx} className="group relative bg-emerald-50/50 border border-emerald-100/50 rounded-xl p-3.5 hover:bg-emerald-50 transition-colors">
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-md bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon name="Check" size={12} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-slate-800 leading-snug">{s.title}</h4>
                  <p className="text-[11px] text-slate-500 font-medium mt-1 leading-relaxed">{s.description}</p>
                  {s.example && (
                    <p className="text-[10px] text-emerald-600 font-semibold mt-1.5 italic">"{s.example}"</p>
                  )}
                  {s.transcriptRef > 0 && (
                    <button
                      onClick={() => handleRefClick(s.transcriptRef)}
                      className="flex items-center gap-1 mt-2 text-[9px] font-black text-emerald-500tracking-widest hover:text-emerald-700 transition-colors"
                    >
                      <Icon name="ExternalLink" size={9} />
                      See evidence (Turn #{s.transcriptRef})
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {safeStrengths.length === 0 && (
            <p className="text-xs text-slate-400 italic text-center py-4">No strengths identified</p>
          )}
        </div>
      </div>

      {/* Weaknesses */}
      <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-1.5 rounded-lg bg-amber-50 border border-amber-100">
            <Icon name="Target" size={14} className="text-amber-500" />
          </div>
          <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Areas to Improve</h3>
          <span className="text-[9px] font-bold text-slate-400 ml-auto">{safeWeaknesses.length} identified</span>
        </div>
        <div className="space-y-3">
          {safeWeaknesses.map((w, idx) => (
            <div key={idx} className="group relative bg-amber-50/30 border border-amber-100/50 rounded-xl p-3.5 hover:bg-amber-50/60 transition-colors">
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-md bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon name="AlertTriangle" size={12} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-slate-800 leading-snug">{w.title}</h4>
                  <p className="text-[11px] text-slate-500 font-medium mt-1 leading-relaxed">{w.description}</p>
                  {w.suggestion && (
                    <div className="flex items-start gap-1.5 mt-2 bg-white rounded-lg p-2 border border-slate-100">
                      <Icon name="Lightbulb" size={10} className="text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-slate-600 font-semibold leading-snug">{w.suggestion}</p>
                    </div>
                  )}
                  {w.transcriptRef > 0 && (
                    <button
                      onClick={() => handleRefClick(w.transcriptRef)}
                      className="flex items-center gap-1 mt-2 text-[9px] font-black text-amber-500tracking-widest hover:text-amber-700 transition-colors"
                    >
                      <Icon name="ExternalLink" size={9} />
                      See evidence (Turn #{w.transcriptRef})
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {safeWeaknesses.length === 0 && (
            <p className="text-xs text-slate-400 italic text-center py-4">No weaknesses identified — great job!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrengthsWeaknessesSection;
